import { useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import "./css/adminquestion.css";
import AdminQuestionDetail from "./modal/AdminQuestionDetail";
import { getQuestionList, searchQuestions } from "../../utils/adminQuestionService";
import { downloadQuestionExcel } from "../../utils/adminExcelService";
import { formatDateTime24 } from "../../utils/commonService";
import Pagination from "../common/Pagination";

const AdminQuestion = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedQuestionIdx, setSelectedQuestionIdx] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [excelLoading, setExcelLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const pageSize = 10;

  const [isSearchMode, setIsSearchMode] = useState(false);

  // 오늘 날짜 구하기
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const [filters, setFilters] = useState({
    startDate: "",
    endDate: getTodayDate(),
    isAnswered: null,
    isMod: null,
    userType: null,
    questionRequestType: null,
    searchType: "all",
    searchKeyword: "",
  });

  useEffect(() => {
    fetchQuestions();
  }, []);

  useEffect(() => {
    if (location.state?.openModal && location.state?.questionIdx) {
      setSelectedQuestionIdx(location.state.questionIdx);
      setIsModalOpen(true);
    }
  }, [location]);

  const fetchQuestions = async (page = 0) => {
    setLoading(true);
    setError(null);
    try {
      const data = await getQuestionList(page, pageSize);
      if (data) {
        setQuestions(data.content || []);
        setCurrentPage(data.number || 0);
        setTotalPages(data.totalPages || 0);
        setTotalElements(data.totalElements || 0);
        setIsSearchMode(false);
      } else {
        setQuestions([]);
      }
    } catch (err) {
      setError(err.message);
      setQuestions([]);
      console.error("문의사항 목록 조회 실패:", err);
    } finally {
      setLoading(false);
    }
  };
  const getQuestionTypeLabel = (type) => {
    const typeMap = {
      QR: "QR 관련",
      PAYMENT: "결제 관련",
      REFUND: "환불/교환 관련",
      MOD_REQUEST: "개인정보 수정 요청",
      ETC: "기타"
    };
    return typeMap[type] || type;
  };
  // 사용자 구분 결정
  const getUserType = (question) => {
    if (question.storeCode) return "가맹점";
    if (question.custCode) return "사용자";
    return "-";
  };
  const handleDetailClick = (questionIdx) => {
    setSelectedQuestionIdx(questionIdx);
    setIsModalOpen(true);
  };
  const handleCloseModal = (shouldRefresh = false) => {
    setIsModalOpen(false);
    setSelectedQuestionIdx(null);
    if (shouldRefresh) {
      if (isSearchMode) {
        handleSearchInternal(currentPage);
      } else {
        fetchQuestions(currentPage);
      }
    }
  };

  const handleSearchInternal = async (page = 0) => {
    const searchParams = {
      startDate: filters.startDate,
      endDate: filters.endDate,
      isAnswered: filters.isAnswered,
      isMod: filters.isMod,
      userType: filters.userType,
      questionRequestType: filters.questionRequestType,
      searchTerm: filters.searchKeyword,
      searchType: filters.searchType,
    };

    setLoading(true);
    setError(null);
    try {
      const data = await searchQuestions(searchParams, page, pageSize);
      if (data) {
        setQuestions(data.content || []);
        setCurrentPage(data.number || 0);
        setTotalPages(data.totalPages || 0);
        setTotalElements(data.totalElements || 0);
        setIsSearchMode(true);
      } else {
        setQuestions([]);
      }
    } catch (err) {
      setError(err.message);
      setQuestions([]);
      console.error("문의사항 검색 실패:", err);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    isSearchMode ? handleSearchInternal(page) : fetchQuestions(page);
  };

  const handleSearch = () => {
    setCurrentPage(0);
    handleSearchInternal(0);
  };

  const handleReset = () => {
    setFilters({
      startDate: "",
      endDate: getTodayDate(),
      isAnswered: null,
      isMod: null,
      userType: null,
      questionRequestType: null,
      searchType: "all",
      searchKeyword: "",
    });
    setCurrentPage(0);
    fetchQuestions(0);
  };
  return (
    <div className="adminquestion-container">
      <div className="adminquestion-wrap">
        <div className="adminquestion-tab-navigation">
          <button className="adminquestion-tab-btn adminquestion-tab-active">
            문의
          </button>
          <button
            className="adminquestion-tab-btn"
            onClick={() => navigate("/admin/report")}
          >
            신고
          </button>
        </div>
        {/* 필터 영역 */}
        <div className="adminquestion-filter-section">
          <div className="adminquestion-filter-row">
            <div className="adminquestion-filter-group">
              <label className="adminquestion-filter-label">조회기간</label>
              <div className="adminquestion-date-range">
                <input 
                  type="date" 
                  className="adminquestion-filter-date"
                  value={filters.startDate}
                  onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                />
                <span className="adminquestion-date-separator">~</span>
                <input 
                  type="date" 
                  className="adminquestion-filter-date"
                  value={filters.endDate}
                  onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                />
              </div>
            </div>
            <div className="adminquestion-checkbox-group">
              <label className="adminquestion-checkbox-label">
                <input 
                  type="checkbox" 
                  className="adminquestion-filter-checkbox"
                  checked={filters.isAnswered === true}
                  onChange={(e) => setFilters({ ...filters, isAnswered: e.target.checked ? true : null })}
                />
                <span>답변완료 여부</span>
              </label>
              <label className="adminquestion-checkbox-label">
                <input 
                  type="checkbox" 
                  className="adminquestion-filter-checkbox"
                  checked={filters.isMod === true}
                  onChange={(e) => setFilters({ ...filters, isMod: e.target.checked ? true : null })}
                />
                <span>수정 여부</span>
              </label>
            </div>
          </div>
          <div className="adminquestion-filter-row">
            <div className="adminquestion-filter-group">
              <label className="adminquestion-filter-label">사용자 구분</label>
              <select 
                name="usertype" 
                id="usertype" 
                className="adminquestion-filter-select"
                value={filters.userType || "userall"}
                onChange={(e) => setFilters({ ...filters, userType: e.target.value === "userall" ? null : e.target.value })}
              >
                <option value="userall">전체</option>
                <option value="cust">일반사용자</option>
                <option value="store">가맹점</option>
              </select>
            </div>
            <div className="adminquestion-filter-group">
              <label className="adminquestion-filter-label">문의타입</label>
              <select 
                name="option" 
                id="option" 
                className="adminquestion-filter-select"
                value={filters.questionRequestType || "all"}
                onChange={(e) => setFilters({ ...filters, questionRequestType: e.target.value === "all" ? null : e.target.value })}
              >
                <option value="all">전체</option>
                <option value="QR">QR 관련</option>
                <option value="PAYMENT">결제 관련</option>
                <option value="REFUND">환불/교환 관련</option>
                <option value="MOD_REQUEST">개인정보 수정 요청</option>
                <option value="ETC">기타</option>
              </select>
            </div>
          </div>
          <div className="adminquestion-filter-divider"></div>
          <div className="adminquestion-filter-row">
            <div className="adminquestion-filter-group">
              <select 
                name="filter" 
                id="filter" 
                className="adminquestion-filter-select"
                value={filters.searchType}
                onChange={(e) => setFilters({ ...filters, searchType: e.target.value })}
              >
                <option value="all">전체</option>
                <option value="regnm">작성자</option>
                <option value="title">제목</option>
              </select>
            </div>
            <div className="adminquestion-search-group">
              <input
                type="text"
                className="adminquestion-filter-input"
                placeholder="검색어를 입력하세요"
                value={filters.searchKeyword}
                onChange={(e) => setFilters({ ...filters, searchKeyword: e.target.value })}
              />
              <button className="adminquestion-filter-search-btn" onClick={handleSearch}>검색</button>
              <button className="adminquestion-filter-reset-btn" onClick={handleReset}>초기화</button>
            </div>
          </div>
        </div>
        <div className="adminquestion-table-header">
          <div className="adminquestion-table-info">
            총 <span className="adminquestion-count-bold">{totalElements}</span>건
          </div>
          <button
            className="adminquestion-excel-btn"
            onClick={async () => {
              setExcelLoading(true);
              const result = await downloadQuestionExcel();
              setExcelLoading(false);
              if (!result.success) alert(result.error);
            }}
            disabled={excelLoading}
          >
            {excelLoading ? "다운로드 중..." : "엑셀 다운로드"}
          </button>
        </div>
        <div className="adminquestion-table-wrap">
          {loading ? (
            <div>로딩 중...</div>
          ) : error ? (
            <div>{error}</div>
          ) : (
            <table className="adminquestion-table">
              <thead>
                <tr>
                  <th>사용자 구분</th>
                  <th>카테고리</th>
                  <th>작성자</th>
                  <th>제목</th>
                  <th>작성일</th>
                  <th>상태</th>
                  <th>답변완료일</th>
                  <th>상세</th>
                </tr>
              </thead>
              <tbody>
                {questions.length === 0 ? (
                  <tr>
                    <td colSpan="9">
                      문의사항이 없습니다.
                    </td>
                  </tr>
                ) : (
                  questions.map((question, index) => (
                    <tr key={question.questionIdx}>
                      <td>{getUserType(question)}</td>
                      <td>{getQuestionTypeLabel(question.questionRequestType)}</td>
                      <td>{question.questionRegNm}</td>
                      <td className="adminquestion-table-title-cell">{question.questionTitle}</td>
                      <td>{formatDateTime24(question.questionRegDateTime)}</td>
                      <td>
                        <span className={`adminquestion-status ${question.isAnswered ? 'adminquestion-status-complete' : 'adminquestion-status-pending'}`}>
                          {question.isAnswered ? '완료' : '대기'}
                        </span>
                      </td>
                      <td>{formatDateTime24(question.answerRegDateTime) || '-'}</td>
                      <td>
                        <button
                          className="adminquestion-detail-btn"
                          onClick={() => handleDetailClick(question.questionIdx)}
                        >
                        보기
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* 페이지네이션 */}
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      </div>
      {/* 문의 상세 모달 */}
      {isModalOpen && selectedQuestionIdx && (
        <AdminQuestionDetail
          questionIdx={selectedQuestionIdx}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
};
export default AdminQuestion;